import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import GoogleButton from "@/components/GoogleButton";
import { api } from "@/lib/api";
import { Sparkles } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [refCreator, setRefCreator] = useState(null); // { name, promo_code }

  // Handle ?ref=slug (from a creator referral link) — resolve slug to promo code
  useEffect(() => {
    const ref = params.get("ref") || (() => { try { return localStorage.getItem("cp_ref_slug"); } catch { return null; } })();
    const codeParam = params.get("code");
    if (codeParam) {
      setPromoCode(codeParam.toUpperCase());
    } else if (ref) {
      api.get(`/creators/${ref}`).then((r) => {
        setPromoCode(r.data.promo_code);
        setRefCreator({ name: r.data.name, promo_code: r.data.promo_code });
      }).catch(() => { /* silent — bad slug just means no code applied */ });
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(email, password, username, promoCode.trim() || undefined);
    setLoading(false);
    if (res.ok) {
      const days = res.data?.trial_days || 14;
      toast.success(days === 21
        ? `Welcome, @${username}! You've got a 21-day free trial thanks to ${res.data.attributed_to}.`
        : `Welcome to Cake Pop Talk, @${username}!`);
      try { localStorage.removeItem("cp_ref_slug"); } catch {}
      nav("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] grid lg:grid-cols-2">
      <div className="grid place-items-center p-6 sm:p-10 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-[#857163] mb-2">Join the community</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24]">
              Bake, share, <span className="text-[#FF7E67]">rise</span>.
            </h1>
            <p className="mt-3 text-[#857163]">A cozy forum for every cake pop enthusiast.</p>
          </div>

          {refCreator && (
            <div className="mb-6 p-3 rounded-2xl bg-[#F4A83B]/15 border border-[#F4A83B]/40 flex items-start gap-2 text-sm text-[#3B2C24]" data-testid="ref-banner">
              <Sparkles className="w-4 h-4 text-[#B26A00] shrink-0 mt-0.5" />
              <span>Referred by <b>{refCreator.name}</b> — you'll get a <b>21-day free trial</b> instead of 14. 🎉</span>
            </div>
          )}

          <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Username</label>
          <input
            className="cp-input mb-4"
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
            required
            minLength={2}
            maxLength={32}
            data-testid="register-username"
          />

          <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Email</label>
          <input
            type="email"
            className="cp-input mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="register-email"
          />

          <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Password</label>
          <input
            type="password"
            className="cp-input mb-6"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            data-testid="register-password"
          />

          <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Promo code <span className="text-[#857163] font-normal">(optional — from a creator)</span></label>
          <input
            className="cp-input mb-6 uppercase"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 40))}
            placeholder="BAKERJANE"
            data-testid="register-promo"
          />
          <p className="text-xs text-[#857163] mb-4">
            By joining, you'll be asked to review and accept our{" "}
            <Link to="/rules" target="_blank" className="text-[#FF7E67] font-semibold hover:underline">
              House Rules
            </Link>
            .
          </p>

          <button
            type="submit"
            disabled={loading}
            className="cp-btn-primary w-full disabled:opacity-70"
            data-testid="register-submit"
          >
            {loading ? "Creating account…" : "Create account"}
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#EBDCD1]" />
            <span className="text-xs uppercase tracking-widest text-[#857163]">or</span>
            <div className="flex-1 h-px bg-[#EBDCD1]" />
          </div>
          <GoogleButton testId="google-signin-register" />

          <p className="mt-6 text-sm text-[#857163] text-center">
            Already a member?{" "}
            <Link to="/login" className="text-[#FF7E67] font-semibold hover:underline" data-testid="link-to-login">
              Log in
            </Link>
          </p>
        </form>
      </div>
      <div
        className="hidden lg:block bg-cover bg-center order-1 lg:order-2"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(255,208,127,0.30), rgba(255,126,103,0.35)), url('https://images.unsplash.com/photo-1586195830864-e4d9688815c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxjYWtlJTIwcG9wcyUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzgzMzgzMDQ2fDA&ixlib=rb-4.1.0&q=85')",
        }}
      />
    </div>
  );
}
